import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import { TouchableWithoutFeedback } from 'react-native';

import { Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import nonCriticalException from '../../../../modules/exceptionLogger';
import debouncer from '../../../../modules/debouncer';
import locationAutocomplete from '../../../../modules/maps/locationAutocomplete';
import analytics from '../../../../modules/analytics';
import Janitor from '../../../../modules/Janitor';
import { BodyText, Absolute } from '../../../ui';
import toHighlightedParts from '../toHighlightedParts';
import { MapsDirection45Icon } from '../../../ui/Icons/MapsDirection45Icon';
import { MapsPinIcon } from '../../../ui/Icons/MapsPinIcon';
import './index.scss';

// a special search "suggestion" displayed when nothing else is returned
const CURRENT_LOCATION_ITEM = {
  isCurrentLocation: true,
  description: 'Current Location',
};

// Re-query geolocation after 1 minute
// This only applies to "Find Me" user action
const MAX_GEOLOCATION_AGE = 60 * 1000;

// For analytics tracking, all the ways a user can update the location
const LOCATION_CONTROL_OPTIONS = {
  SELECTED_FIND_ME: 'find-me',
  SELECTED_CURRENT_LOCATION_OPTION: 'current-location-item',
  ENTERED_LOCATION_MANUALLY: 'user-manually-entered',
  SELECTED_AUTOCOMPLETE_SUGGESTION: 'autocomplete-suggestion-selected',
};

// For analytics tracking, the method by which location is loaded
const LOCATION_SOURCE = {
  PREFILLED: 'text_prefilled',
  GPS: 'gps',
};

/**
 * Return what the input field value should be for an autocomplete suggestion.
 */
function getSuggestionValue(suggestion) {
  if (!suggestion.isCurrentLocation) {
    return suggestion.description;
  }
  return '';
}

/**
 * Given an autocomplete suggestion, return the suggestion text, including any areas that need to
 * be emphasized, as a react element.
 * @param {Object} suggestion - The location autocomplete suggestion item.
 * @return {ReactComponent} The formatted text.
 */
function formatSuggestion(suggestion) {
  if (suggestion.isCurrentLocation) {
    return 'Use My Current Location';
  }

  const matches = toHighlightedParts(
    suggestion.description,
    suggestion.matched_substrings,
  );
  return matches.map(({
    text, isMatch, idx,
  }) => (
    <span key={idx}>{isMatch ? <strong>{text}</strong> : text}</span>
  ));
}

/**
 * The location picker popover for search.
 *
 * It includes an autocomplete for locations and functionality to get the user's
 * device location.
 *
 * The location the user selects get sent to the SearchConfig redux state.
 *
 * @example
 * import LocationFilter from 'components/consumer/search/LocationFilter';
 *
 * const MyComponent = () => {
 *   return (
 *     <LocationFilter />
 *   );
 * }
 */
class LocationFilter extends React.Component {
  static propTypes = {
    address: PropTypes.string,
    addressComponents: PropTypes.object,
    updateLocation: PropTypes.func.isRequired,
    nextSearchID: PropTypes.string,
    loadGeolocation: PropTypes.func,
    geolocation: PropTypes.object,
    inputRef: PropTypes.any,
    style: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
  };

  static defaultProps = {
    address: null,
    addressComponents: null,
    nextSearchID: null,
    loadGeolocation: () => {},
    geolocation: {},
    inputRef: null,
    style: null,
  };

  state = {
    text: this.props.address,
    suggestions: [CURRENT_LOCATION_ITEM],
    lastTypedText: '',
    control: null,
    focused: false,
    locationHasChanged: false,
    locationError: null,
  };

  janitor = new Janitor();

  apiDebounce = debouncer();

  autocompletePromise = null;

  currentLocationPromise = null;

  lastSuggestionResults = [];

  componentDidMount() {
    if (!this.props.address) {
      this.handleGeolocationUpdate();
    }
  }

  componentDidUpdate(prevProps) {
    const { loadingCurrentLocation } = this.state;
    const {
      geolocation: { loading: wasLoading },
    } = prevProps;
    const { geolocation } = this.props;
    if (loadingCurrentLocation && wasLoading && !geolocation.loading) {
      this.handleGeolocationUpdate();
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
    this.apiDebounce.stop();
  }

  /**
   * Send the `client_locationpicker_submitted` tracking event, plus any additional tracking
   * params.
   * @param {Object} location - Object with location information
   * @param {String} control - A unique key representing the thing that initiated the submit.
   * @param {String} source - The source of the location data
   */
  trackSubmit(location, source = 'text_prefilled') {
    const {
      lastTypedText, text, control,
    } = this.state;
    const { nextSearchID } = this.props;

    analytics.track('client_locationpicker_submitted', {
      control,
      search_text: text,
      select_type: source,
      last_typed_search_text: lastTypedText,
      search_id: nextSearchID,
      location: {
        address: location.description,
        latitude: location.latitude,
        longitude: location.longitude,
        addressComponents: location.addressComponents,
      },
    });
  }

  isGeolocationExpired() {
    const {
      geolocation: { created = 0 },
    } = this.props;
    return created < Date.now() - MAX_GEOLOCATION_AGE;
  }

  async handleGeolocationUpdate() {
    const { geolocation } = this.props;
    if (geolocation.address && !this.isGeolocationExpired()) {
      await this.setLocation(geolocation, LOCATION_SOURCE.GPS);
      this.setState({
        text: geolocation.address,
        locationError: false,
        loadingCurrentLocation: false,
      });
    } else {
      this.setState({
        loadingCurrentLocation: false,
      });
    }
  }

  /**
   * Asynchronously updates the location using the `updateLocation` prop.
   * @param {LocationRecord} location The location information to set
   * @param {string} source The origin of the location information
   */
  async setLocation(location, source) {
    try {
      await this.props.updateLocation(location);
      this.trackSubmit(location, source);
    } catch (e) {
      nonCriticalException(e);
    }
  }

  /**
   * Search for the user's location, and submit a new search if it's found
   * @param {String} trackControl - The item to be tracked as the 'control' for this action.
   */
  async useCurrentLocation() {
    const {
      geolocation: { loading },
      loadGeolocation,
    } = this.props;
    // If geolocation is old or not loaded, load it now
    // the update is handled in componentDidUpdate
    if (!loading && this.isGeolocationExpired()) {
      this.setState({
        loadingCurrentLocation: true,
        suggestions: [],
      });

      const newLocation = await loadGeolocation();
      if (newLocation === null) {
        this.setState({
          loadingCurrentLocation: false,
          locationError: true,
          locationHasChanged: true,
          text: '',
        });
      }
    } else if (!loading) {
      // If geolocation is loaded and not expired, use that
      this.handleGeolocationUpdate();
    }
  }

  /**
   * A location suggestion was selected.
   * See: https://github.com/moroshko/react-autosuggest#on-suggestion-selected-prop
   * @param {Object} suggestion
   * @param {Boolean} manualSelection - False if location picked from suggestion drop down.
   *                                    True if manually entered and submitted.
   */
  handleSuggestionSelected = (_, result, manualSelection = false) => {
    if (!result) {
      return;
    }
    const { suggestion } = result;
    let selection = 'autocomplete-suggestion-selected';
    if (manualSelection) {
      selection = 'user-manually-entered';
    }

    // Current Location
    if (suggestion.isCurrentLocation) {
      this.setState(
        {
          control: LOCATION_CONTROL_OPTIONS.SELECTED_FIND_ME,
          focused: false,
        },
        this.useCurrentLocation,
      );
    } else {
      this.setState(
        {
          control: selection,
          focused: false,
        },
        () => {
          this.setLocation(
            {
              address: suggestion.description,
              placeId: suggestion.place_id,
            },
            LOCATION_SOURCE.PREFILLED,
          );
        },
      );
    }
  };

  /**
   * Clear all the suggestions and just show the current location button.
   */
  handleClearRequest = () => {
    this.setState({ suggestions: [CURRENT_LOCATION_ITEM] });
  };

  /**
   * Query the google API to get the location autocomplete suggestions.
   */
  handleSuggestionFetchRequest = ({ value }) => {
    if (value) {
      // Cancel current location request
      if (this.currentLocationPromise && this.currentLocationPromise.cancel) {
        this.currentLocationPromise.cancel();
        this.currentLocationPromise = null;
      }

      this.apiDebounce(() => {
        this.setState({
          loadingCurrentLocation: false,
        });

        this.autocompletePromise = this.janitor
          .addPromise(locationAutocomplete(value, ['geocode']))
          .then(suggestions => {
            this.lastSuggestionResults = suggestions;

            this.setState({
              suggestions: [
                CURRENT_LOCATION_ITEM,
                ...suggestions,
              ],
            });
          })
          .finally(() => {
            this.autocompletePromise = null;
          });
      });
    } else {
      this.setState({ suggestions: [CURRENT_LOCATION_ITEM] });
    }
  };

  /**
   * Called when the value of the location field changes.
   * See: https://github.com/moroshko/react-autosuggest#input-props-prop
   */
  handleFieldChange = (evt, { newValue, method }) => {
    const { locationHasChanged } = this.state;
    let { lastTypedText } = this.state;

    // if they're typing, keep their entered value around for analytics
    if (method === 'type') {
      lastTypedText = newValue;
    }

    if (method === 'escape') {
      this.setState({ focused: false, locationHasChanged });
      evt.target.blur();
    } else if (method !== 'click' || newValue) {
      // update text as long as we're not trying to find current location
      this.setState({
        text: newValue,
        locationHasChanged: true,
        lastTypedText,
      });
    }
  };

  handleFieldFocus = evt => {
    evt.target.select();
    this.setState({
      focused: true,
      suggestions: [CURRENT_LOCATION_ITEM],
    });
  };

  handleFieldBlur = () => {
    this.setState({ focused: false });
    const { lastTypedText, suggestions } = this.state;
    const suggestion = suggestions?.[1];
    if (suggestion && lastTypedText) {
      this.setLocation(
        {
          address: suggestion.description,
          placeId: suggestion.place_id,
        },
        LOCATION_SOURCE.PREFILLED,
      );
    }
  };

  /**
   * Render the header element, which will display the input box and search button.
   */
  renderInputComponent = inputProps => (
    <header>
      <section className={classNames('location-input')}>
        <Absolute left={11}>
          <MapsPinIcon color={colors.blackInBlack} />
        </Absolute>
        <input {...inputProps} />
      </section>
    </header>
  );

  /**
   * Render an individual location suggestion.
   */
  renderSuggestion = (suggestion, { isHighlighted }) => {
    const { isCurrentLocation } = suggestion;
    return (
      <TouchableWithoutFeedback>
        <Inline
          paddingY={12}
          paddingRight={20}
          paddingLeft={isCurrentLocation ? 24 : 40}
          color={isHighlighted ? colors.chill : colors.whiteWash}
          borderBottomColor={colors.lightGrey}
          borderBottomWidth={1}
          borderStyle="solid"
          space={4}
        >
          {isCurrentLocation && (
            <MapsDirection45Icon
              color={colors.blackInBlack}
              width={16}
              height={16}
            />
          )}
          <BodyText>{formatSuggestion(suggestion)}</BodyText>
        </Inline>
      </TouchableWithoutFeedback>
    );
  };

  render() {
    const {
      text,
      suggestions,
      focused,
      locationHasChanged,
      locationError,
    } = this.state;

    const {
      addressComponents, address, style,
    } = this.props;

    let locale = address || '';
    if (addressComponents) {
      locale = addressComponents.city;
      if (addressComponents.neighborhood) {
        locale = addressComponents.neighborhood;
      }
    } else {
      locale = locale.split(',')[0];
    }

    return (
      <section
        className={classNames('location-filter-component', {
          focused,
          error: locationError,
        })}
        style={style}
      >
        <Autosuggest
          inputProps={{
            value: locationHasChanged ? text : locale,
            onChange: this.handleFieldChange,
            onFocus: this.handleFieldFocus,
            onBlur: this.handleFieldBlur,
            autoComplete: 'disabled',
            placeholder: locationError
              ? 'Please enable location sharing'
              : 'City or Zip Code',
            'data-testid': 'location-input',
          }}
          ref={this.props.inputRef}
          alwaysRenderSuggestions={focused}
          focusInputOnSuggestionClick={false}
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.handleSuggestionFetchRequest}
          onSuggestionsClearRequested={this.handleClearRequest}
          onSuggestionSelected={this.handleSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          renderInputComponent={this.renderInputComponent}
        />
      </section>
    );
  }
}

const mapStateToProps = ({ searchConfig, location }) => ({
  addressComponents: searchConfig.addressComponents,
  geolocation: location,
});

const mapDispatchToProps = ({ location }) => ({
  loadGeolocation: location.loadGeolocation,
});

export const ConnectedLocationFilter = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LocationFilter);

export const LocationFilterWithRef = React.forwardRef((props, ref) => (
  <ConnectedLocationFilter {...props} inputRef={ref} />
));

// eslint-disable-next-line react/static-property-placement
LocationFilterWithRef.propTypes = LocationFilter.propTypes;
// eslint-disable-next-line react/static-property-placement
LocationFilterWithRef.defaultProps = LocationFilter.defaultProps;

export default LocationFilterWithRef;
